import CheckIcon from "@mui/icons-material/Check";
import { Box, Button, Dialog, Typography } from "@mui/material";
import { SmartAutoComplete } from "@nodeon/react-components";
import React from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Coordinate } from "~/api/GeneratedApi";
import { useEventEmitterOnListener } from "~/utils/hooks/useEventEmitterOnListener";
import { PolygonCoords } from "~/utils/hooks/usePolygonDraw";
import { AreaDrawDialog } from "../AreaDrawDialog";
import { useActionSearchOptions } from "../hooks/useActionSearchOptions";
import { useDropdownOptions } from "../hooks/useDropdownOptions";
import { SharedFormProps } from "../WaterTrafficSignContainers/WaterTrafficSignStateForm";

export type LocationAndResponsibilityFormProps = {
  contractArea: string | null;
  contractor: string | null;
  owner: string | null;
  fairway: string | null;
  fairwayManagementClass: string | null;
  fairwayClass: string | null;
  points: Coordinate[];
};

export const locationAndResponsibilityFormDefaults: LocationAndResponsibilityFormProps =
  {
    contractArea: null,
    contractor: null,
    owner: null,
    fairway: null,
    fairwayClass: null,
    fairwayManagementClass: null,
    points: [],
  };

export const LocationAndResponsibilityForm = ({
  isDisabled,
}: SharedFormProps) => {
  const { searchResult, isLoading } = useDropdownOptions();
  const { t } = useTranslation("fairwayReport");
  const { control, setValue } =
    useFormContext<LocationAndResponsibilityFormProps>();
  const [open, setOpen] = React.useState<boolean>(false);
  const [mapPoints, setMapPoints] = React.useState<PolygonCoords>([]);
  const { actionDropDown } = useActionSearchOptions();
  const handleMapPointSet = React.useCallback(
    (points: PolygonCoords) => {
      setMapPoints(points);

      const coordinatePoint = points[0]?.map((c) => ({ x: c[0], y: c[1] }));
      setValue("points", coordinatePoint);
    },
    [setValue],
  );

  const handleClose = React.useCallback(() => {
    setOpen(false);
    // setValue("points", []);
  }, []);

  // React.useEffect(() => {
  //   register("points");
  // }, [register]);

  const handleClear = React.useCallback(() => {
    setMapPoints([]);

    setValue("points", []);
  }, [setMapPoints, setValue]);

  useEventEmitterOnListener("clear-search", handleClear);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <SmartAutoComplete<LocationAndResponsibilityFormProps, string, false>
          disabled={isDisabled}
          name="contractArea"
          label={t("contractArea")}
          options={searchResult?.jobArea ?? []}
          controllerProps={{ control }}
          loading={isLoading}
        />
        <SmartAutoComplete<LocationAndResponsibilityFormProps, string, false>
          disabled={isDisabled || true}
          name="contractor"
          label={t("contractor")}
          options={actionDropDown?.contractor ?? []}
          controllerProps={{ control }}
          loading={isLoading}
        />
        <SmartAutoComplete<LocationAndResponsibilityFormProps, string, false>
          disabled={isDisabled}
          name="owner"
          label={t("owner")}
          options={searchResult?.owners ?? []}
          controllerProps={{ control }}
          loading={isLoading}
        />
        <SmartAutoComplete<LocationAndResponsibilityFormProps, string, false>
          disabled={isDisabled}
          name="fairway"
          loading={isLoading}
          label={t("fairway")}
          options={searchResult?.fairways ?? []}
          controllerProps={{ control }}
        />
        <SmartAutoComplete<LocationAndResponsibilityFormProps, string, false>
          disabled={isDisabled || true}
          name="fairwayClass"
          label={t("fairwayClass")}
          options={["Optio 1", "Optio 2", "Optio 3"]}
          controllerProps={{ control }}
          loading={isLoading}
        />
        <SmartAutoComplete<LocationAndResponsibilityFormProps, string, false>
          disabled={isDisabled || true}
          name="fairwayManagementClass"
          label={t("fairwayManagementClass")}
          options={["Optio 1", "Optio 2", "Optio 3"]}
          controllerProps={{ control }}
          loading={isLoading}
        />
        <Button
          onClick={() => setOpen(true)}
          sx={{ m: "auto" }}
          variant="contained"
          color={mapPoints.length > 0 ? "success" : "primary"}
          endIcon={mapPoints.length > 0 ? <CheckIcon /> : undefined}
        >
          <Typography> {t("fairwayReport:selectAreaFromMap")}</Typography>
        </Button>
        {/* <input hidden {...register("points")} /> */}
      </Box>
      <Dialog
        fullWidth
        maxWidth="xl"
        onClose={handleClose}
        open={open}
        PaperProps={{ sx: { height: "85%" } }}
      >
        <AreaDrawDialog
          mapPoints={mapPoints}
          handleClose={handleClose}
          setMapPoints={handleMapPointSet}
          setValue={setValue}
        />
      </Dialog>
    </>
  );
};
